/* ===========================================================================
   Turning OCR strings into evidence — cardvision/recognize/text.py.
   =========================================================================== */

export const COLLECTOR_RE =
  /(?:^|[^0-9A-Za-z])(?<number>[0-9]{1,4}[a-zA-Z]?)\s*(?:\/\s*[0-9]{1,4})?(?:$|[^0-9])/;
export const RARITY_RE = /\b([CURMSTPLB])\b/;
export const SET_RE = /\b(?<set>[0-9A-Z]{3,6})\b\s*[^0-9A-Za-z]{0,3}\s*(?<lang>[A-Z]{2})\b/;

//: Characters OCR most often swaps inside the digit-only collector number.
export const DIGIT_FIXES = { O: "0", o: "0", I: "1", l: "1", S: "5", B: "8", Z: "2" };
//: The same confusions folded the other way for names, so both sides of a comparison
//: land on one representative instead of an edit apart.
export const CONFUSION_FOLD = { 0: "o", 1: "l", i: "l", 5: "s", 8: "b", 2: "z" };

export const translate = (value, table) =>
  value.replace(/./g, (ch) => (Object.prototype.hasOwnProperty.call(table, ch) ? table[ch] : ch));

/** text.normalize_name — casefold, strip accents and punctuation. */
export function normalizeName(value) {
  return value
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * rapidfuzz's `fuzz.ratio` — normalised indel similarity, which is 2 x LCS over the
 * summed lengths. Exact rather than approximated: the accept threshold is calibrated
 * against this number in Python, and a differently-shaped "similarity" would quietly
 * move the operating point.
 */
export function indelRatio(a, b) {
  if (!a.length && !b.length) return 1;
  if (!a.length || !b.length) return 0;
  let previous = new Int32Array(b.length + 1);
  let current = new Int32Array(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    current[0] = 0;
    for (let j = 1; j <= b.length; j++) {
      current[j] =
        a[i - 1] === b[j - 1] ? previous[j - 1] + 1 : Math.max(previous[j], current[j - 1]);
    }
    [previous, current] = [current, previous];
  }
  return (2 * previous[b.length]) / (a.length + b.length);
}

/** text.name_similarity — each face of a double-faced name scored separately. */
export function nameSimilarity(read, name) {
  const readNormalized = translate(normalizeName(read), CONFUSION_FOLD);
  if (!readNormalized) return 0;
  const parts = name.includes("//") ? [name, ...name.split("//")] : [name];
  return Math.max(
    ...parts.map((part) =>
      indelRatio(readNormalized, translate(normalizeName(part), CONFUSION_FOLD)),
    ),
  );
}

/** text.parse_collector_block — the bottom-left "R 0001" over "OTJ • EN <artist>". */
export function parseCollectorBlock(collectorLine, setLine = "") {
  const combined = `${collectorLine}\n${setLine}`.trim();
  let number = "";
  const match = COLLECTOR_RE.exec(translate(collectorLine, DIGIT_FIXES));
  if (match) number = match.groups.number.replace(/^0+/, "") || "0";

  const rarityMatch = RARITY_RE.exec(collectorLine.toUpperCase());
  const setMatch = SET_RE.exec((setLine || combined).toUpperCase());
  return {
    collectorNumber: number,
    rarity: rarityMatch ? rarityMatch[1] : "",
    setCode: setMatch ? setMatch.groups.set.toLowerCase() : "",
    lang: setMatch ? setMatch.groups.lang.toLowerCase() : "",
  };
}

/** text.collector_numbers_match — "0001" is "1", but "218a" is never "218". */
export function collectorNumbersMatch(read, printed) {
  if (!read || !printed) return false;
  const a = read.trim().toLowerCase().replace(/^0+/, "") || "0";
  const b = printed.trim().toLowerCase().replace(/^0+/, "") || "0";
  return a === b || a === b.split("-").pop();
}

//: Alphanumeric characters a title read must reach before it counts as a read at all —
//: pipeline._MIN_TITLE_CHARS. A skewed, partial or glare-blown crop typically yields
//: one or two stray glyphs: long enough to be a string, far too short to be a name.
export const MIN_TITLE_CHARS = 3;

/**
 * Whether the OCR pass read anything worth deciding on — pipeline._ocr_was_legible.
 *
 * A failed read is absence of evidence, not evidence against: scored anyway, it drags
 * a correct answer below the accept threshold (measured in Python: 0.733 before the
 * OCR stage, 0.468 after it, on a card whose crop OCR could not read). A printed
 * identifier is a read whatever the title did; otherwise the title has to carry it.
 */
export function ocrWasLegible(text, info) {
  if (info && (info.collectorNumber || info.setCode)) return true;
  const title = (text.title.text || "").replace(/[^\p{L}\p{N}]+/gu, "");
  return title.length >= MIN_TITLE_CHARS;
}
